import { usePutSortIcbtRequests } from "api/useIcbtQueries";
import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { useDnD } from "hooks/useDnD";
import { useSnackbar } from "@mindlercare/ui-web";
import { useTranslation } from "react-i18next";
import { useFlag } from "@mindlercare/unleash-client";
import { StyledTr, StyledTrProps } from "components/Table/styles";
import { AssignedTableRow } from "./useIcbtTableData";
import { usePsychologistUser } from "hooks";
import { useUserHasRole } from "hooks/userHooks";

type Props = {
  userId: number | undefined;
  data: AssignedTableRow[];
};

/**
 * A hook to handle the drag and drop logic for table data
 */
export const useSortableTable = ({ userId, data }: Props) => {
  const sortableIcbtsEnabled = useFlag("PSYCHOLOGIST_SORTABLE_ICBTS");

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const isPsychologist = useUserHasRole("Psychologist");
  const { psychologist } = usePsychologistUser();

  const { mutateAsync: putSortIcbtRequests, isPending: isMutating } =
    usePutSortIcbtRequests();

  const [draggedItems, setDraggedItems] = useState<AssignedTableRow[] | null>(
    null,
  );

  const handleSortCancel = useCallback(() => {
    setDraggedItems(data);
  }, [data]);

  const handleSortSave = useCallback(async () => {
    if (!draggedItems) return;

    if (!userId) {
      enqueueSnackbar({ text: t("SORTING_SAVE_FAILED"), isError: true });
      throw new Error("No user id!");
    }

    if (!isPsychologist) {
      enqueueSnackbar({ text: t("SORTING_SAVE_FAILED"), isError: true });
      throw new Error("Editing user is not a psychologist!");
    }

    const pairs = draggedItems.map((row) => Number(row.id));

    try {
      await putSortIcbtRequests({
        sortedRequestIds: pairs,
        userId,
        initiatedByUserId: psychologist.user.userId,
      });
      enqueueSnackbar({ text: t("SORTING_SAVED") });
    } catch {
      enqueueSnackbar({ text: t("SORTING_SAVE_FAILED"), isError: true });
    } finally {
      setDraggedItems(null);
    }
  }, [
    draggedItems,
    enqueueSnackbar,
    putSortIcbtRequests,
    psychologist,
    isPsychologist,
    t,
    userId,
  ]);

  const handleDragEnd = useCallback(
    async (eventData: AssignedTableRow[]) => {
      const prevIds = data.map((row) => row.id);
      const nextIds = eventData.map((row) => row.id);

      const equal = prevIds.every((value, index) => value === nextIds[index]);

      if (equal) {
        setDraggedItems(null);
        return;
      }

      setDraggedItems(eventData);
    },
    [data],
  );

  const rowsWithIds = useMemo(() => {
    return (draggedItems ?? data).map((row) => ({
      ...row,
      id: row.id,
    }));
  }, [data, draggedItems]);

  const {
    data: tableData,
    RootContext,
    RowComponent,
    SortableContext,
  } = useDnD({
    data: rowsWithIds,
    onDragEnd: handleDragEnd,
  });

  return {
    data: tableData,
    hasDraggedItems: !!draggedItems,
    handleSortCancel,
    handleSortSave,
    isMutating,
    Components: {
      RootContext: sortableIcbtsEnabled
        ? RootContext
        : (props: PropsWithChildren) => <>{props.children}</>,
      SortableContext: sortableIcbtsEnabled
        ? SortableContext
        : (props: PropsWithChildren) => <>{props.children}</>,
      RowComponent: sortableIcbtsEnabled
        ? RowComponent
        : (props: PropsWithChildren<StyledTrProps>) => (
            <StyledTr {...props}>{props.children}</StyledTr>
          ),
    },
  };
};
